import { queryOptions } from '@tanstack/react-query';

import { octokit } from '@/lib/clients';

export type Repository = Awaited<ReturnType<typeof octokit.rest.repos.get>>['data'];
export type GithubCollaborator = Awaited<ReturnType<typeof octokit.rest.repos.listCollaborators>>['data'][number];

export const repositoryQuery = (owner: string, repo: string) =>
  queryOptions({
    queryKey: [owner, repo, 'repository'],
    queryFn: async () => {
      const { data } = await octokit.rest.repos.get({
        owner,
        repo,
      });
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

export const collaboratorsQuery = (owner: string, repo: string) =>
  queryOptions({
    queryKey: [owner, repo, 'collaborators'],
    queryFn: async () => {
      const collaborators = await octokit.paginate(octokit.rest.repos.listCollaborators, {
        owner,
        repo,
        per_page: 100,
      });

      const bots: GithubCollaborator[] = [createBot('mesa-dot-dev', 'https://github.com/mesa-dot-dev.png', 'Bot')];
      if (import.meta.env.DEV) {
        bots.push(createBot('mesa-dev-development', 'https://avatars.githubusercontent.com/u/186092037?v=4', 'Bot'));
      }

      const existingLogins = new Set(collaborators.map((c) => c.login));
      const uniqueBots = bots.filter((bot) => !existingLogins.has(bot.login));

      return [...uniqueBots, ...collaborators];
    },
    staleTime: Infinity,
  });

export const branchQuery = (owner: string, repo: string, branch: string) => {
  queryOptions({
    queryKey: [owner, repo, 'branches', branch],
    queryFn: async () => {
      const { data } = await octokit.rest.repos.getBranch({ owner, repo, branch });
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const branchesQuery = (owner: string, repo: string) =>
  queryOptions({
    queryKey: [owner, repo, 'branches'],
    queryFn: async () => {
      // Paginate all branches
      const branches = await octokit.paginate(octokit.rest.repos.listBranches, {
        owner,
        repo,
        per_page: 100,
      });
      return branches;
    },
    staleTime: 1000 * 60 * 5,
  });

function createBot(login: string, avatar_url: string, type: string): GithubCollaborator {
  return {
    login: login,
    avatar_url: avatar_url,
    type: type,
    id: 0,
    node_id: '',
    gravatar_id: null,
    url: '',
    html_url: '',
    followers_url: '',
    following_url: '',
    gists_url: '',
    starred_url: '',
    subscriptions_url: '',
    organizations_url: '',
    repos_url: '',
    events_url: '',
    received_events_url: '',
    site_admin: false,
    role_name: '',
  };
}
